import { formatCurrency } from '@/lib/formatters';
import type { DiretoriaKpiTone } from '@/lib/diretoriaKpiTone';

export type DiretoriaExecutiveSource = 'api' | 'erp';

export interface DiretoriaExecutiveCard {
  id: string;
  label: string;
  value: string;
  sublabel?: string;
  trend?: string;
  trendPositive?: boolean;
  valueTone?: DiretoriaKpiTone;
  source: DiretoriaExecutiveSource;
}

export interface DiretoriaDashboardStats {
  totalTitulos?: number;
  titulosAbertos?: number;
  titulosVencidos?: number;
  clientesInadimplentes?: number;
  valorTotal?: number;
  operadoresAtivos?: number;
  empresasAtivas?: number;
  aging1a2Dias?: { count?: number; valor?: number };
  aging3a6Dias?: { count?: number; valor?: number };
  agingAcima7Dias?: { count?: number; valor?: number };
  titulosPorEmpresa?: Array<{ empresa: string; count: number; valor: number }>;
}

export interface DiretoriaFaturamentoMesStats {
  totalPago: number;
  ano: number;
  mes: number;
  companyId?: string;
  empresaid?: number;
  querySql?: string;
}

export interface DiretoriaReceitaProjetadaStats {
  totalAReceber: number;
  competencias: Array<{ ano: number; mes: number; competencia: string; totalAReceber: number }>;
  companyId?: string;
  empresaid?: number;
  querySql?: string;
}

function formatMonthYear(mes?: number, ano?: number): string {
  if (!mes || !ano) return 'ERP FlyStart';
  return `${String(mes).padStart(2, '0')}/${ano}`;
}

export function buildExecutiveCards(
  dashboard: DiretoriaDashboardStats | null | undefined,
  faturamento: DiretoriaFaturamentoMesStats | null | undefined,
  receitaProjetada: DiretoriaReceitaProjetadaStats | null | undefined
): DiretoriaExecutiveCard[] {
  const valorCarteira = Number(dashboard?.valorTotal ?? 0);
  const titulosVencidos = Number(dashboard?.titulosVencidos ?? 0);
  const clientesInad = Number(dashboard?.clientesInadimplentes ?? 0);
  const agingLeve = Number(dashboard?.aging1a2Dias?.count ?? 0);
  const competencias = receitaProjetada?.competencias?.length ?? 0;

  return [
    {
      id: 'exec-faturamento-mes',
      label: 'Faturamento (mês)',
      value: faturamento != null ? formatCurrency(Number(faturamento.totalPago ?? 0)) : '—',
      sublabel: faturamento
        ? `${formatMonthYear(faturamento.mes, faturamento.ano)} · ERP FlyStart`
        : 'ERP FlyStart',
      valueTone: faturamento && faturamento.totalPago > 0 ? 'good' : 'neutral',
      source: 'erp',
    },
    {
      id: 'exec-valor-carteira',
      label: 'Valor em carteira',
      value: dashboard != null ? formatCurrency(valorCarteira) : '—',
      sublabel: dashboard != null ? 'Valor em títulos (cobrança)' : 'Cobrança',
      valueTone: valorCarteira > 0 ? 'bad' : 'neutral',
      source: 'api',
    },
    {
      id: 'exec-titulos-vencidos',
      label: 'Títulos vencidos',
      value: dashboard != null ? String(titulosVencidos) : '—',
      sublabel: dashboard != null ? 'Vencidos / parciais (cobrança)' : 'Cobrança',
      valueTone: titulosVencidos === 0 ? 'good' : 'bad',
      source: 'api',
    },
    {
      id: 'exec-clientes-inad',
      label: 'Clientes inadimplentes',
      value: dashboard != null ? String(clientesInad) : '—',
      sublabel: dashboard != null ? 'Com título vencido (cobrança)' : 'Cobrança',
      valueTone: clientesInad === 0 ? 'good' : 'bad',
      source: 'api',
    },
    {
      id: 'exec-aging-leve',
      label: 'Vencidos 1–2 dias',
      value: dashboard != null ? String(agingLeve) : '—',
      sublabel: dashboard != null ? 'Faixa leve (cobrança)' : 'Cobrança',
      valueTone: agingLeve === 0 ? 'good' : 'bad',
      source: 'api',
    },
    {
      id: 'exec-receita-projetada',
      label: 'Receita projetada',
      value: receitaProjetada != null ? formatCurrency(Number(receitaProjetada.totalAReceber ?? 0)) : '—',
      sublabel:
        receitaProjetada != null
          ? competencias > 0
            ? `${competencias} competência${competencias === 1 ? '' : 's'} · ERP FlyStart`
            : 'Hoje em diante · ERP FlyStart'
          : 'ERP FlyStart',
      valueTone: 'neutral',
      source: 'erp',
    },
  ];
}

export function dashboardToAgingChart(stats: DiretoriaDashboardStats | null | undefined) {
  if (!stats) return null;
  return [
    {
      name: '1–2 dias',
      valor: Number(stats.aging1a2Dias?.valor ?? 0),
      valor2: Number(stats.aging1a2Dias?.count ?? 0),
    },
    {
      name: '3–6 dias',
      valor: Number(stats.aging3a6Dias?.valor ?? 0),
      valor2: Number(stats.aging3a6Dias?.count ?? 0),
    },
    {
      name: '7+ dias',
      valor: Number(stats.agingAcima7Dias?.valor ?? 0),
      valor2: Number(stats.agingAcima7Dias?.count ?? 0),
    },
  ];
}

export function dashboardToEmpresaBar(stats: DiretoriaDashboardStats | null | undefined, take = 8) {
  const rows = stats?.titulosPorEmpresa;
  if (!Array.isArray(rows) || rows.length === 0) return [];
  return [...rows]
    .sort((a, b) => Number(b.valor ?? 0) - Number(a.valor ?? 0))
    .slice(0, take)
    .map((r) => ({
      name: r.empresa?.length > 18 ? `${r.empresa.slice(0, 16)}…` : r.empresa,
      valor: Number(r.count ?? 0),
      valor2: Number(r.valor ?? 0),
    }));
}
